import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import { fetchAllSpots } from '../../store/slices/savedSpotsSlice';
import { fetchAlertPreferences, updateAlertPreferences, fetchAlertReport } from '../../store/slices/alertPreferencesSlice';
import Checkbox from '../../components/fields/Checkbox';
import DaysOfWeekPicker from '../../components/fields/DaysOfWeekPicker';
import DualSlider from '../../components/fields/DualSlider';
import GroupByPicker from '../../components/fields/GroupByPicker';
import { getOrderedSpots, getDirectionText } from '../../utils/helpers';
import {Helmet} from "react-helmet";

// Marine forecast data is only reliably available for this many days ahead
const MAX_FORECAST_DAYS = 10;

// 'D, M j' equivalent of the email's Carbon format, e.g. "Wed, Oct 1"
const formatDigestDate = (dateStr) => {
    const date = new Date(`${dateStr}T00:00:00`);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Directions are reported as "from", so flip 180° to point the arrow where it's heading -
// mirrors the rotate(fmod(...)) formula in digest.blade.php
const directionArrowRotation = (degrees) => ((degrees + 180) % 360 + 360) % 360;

const Alerts = () => {
    const dispatch = useDispatch();
    const savedSpots = useSelector((state) => state.savedSpots.spots);
    const spotOrder = useSelector((state) => state.savedSpots.spotOrder);
    const { preferences } = useSelector((state) => state.alertPreferences);
    const { report, isLoadingReport } = useSelector((state) => state.alertPreferences);

    const [activeTab, setActiveTab] = useState('settings');
    const [reportGroupBy, setReportGroupBy] = useState('spot');
    const reportGroupByInitializedRef = useRef(false);

    const [formData, setFormData] = useState({
        enabled: false,
        spot_ids: [],
        days_of_week: [],
        forecast_days: MAX_FORECAST_DAYS,
        min_rating: 3.5,
        start_hour: 5,
        end_hour: 20,
        group_by: 'spot',
    });
    const skipNextSaveRef = useRef(true);
    // Guards against React 18 StrictMode's dev-only double-invoke of effects on mount, which
    // otherwise lets a second invocation slip past skipNextSaveRef before preferences ever load
    // and auto-save the still-blank defaults, overwriting real saved preferences.
    const preferencesLoadedRef = useRef(false);

    useEffect(() => {
        dispatch(fetchAllSpots());
        dispatch(fetchAlertPreferences());
    }, [dispatch]);

    useEffect(() => {
        if (activeTab === 'report') dispatch(fetchAlertReport());
    }, [dispatch, activeTab]);

    // Default the report's view to the saved email setting, but only once - after that,
    // a manual toggle should survive subsequent "Refresh" clicks within the session.
    useEffect(() => {
        if (report?.group_by && !reportGroupByInitializedRef.current) {
            reportGroupByInitializedRef.current = true;
            setReportGroupBy(report.group_by);
        }
    }, [report]);

    useEffect(() => {
        if (preferences) {
            preferencesLoadedRef.current = true;
            skipNextSaveRef.current = true;
            setFormData({
                enabled: preferences.enabled || false,
                spot_ids: preferences.spot_ids || [],
                days_of_week: preferences.days_of_week || [],
                forecast_days: preferences.forecast_days || MAX_FORECAST_DAYS,
                min_rating: preferences.min_rating ?? 3.5,
                start_hour: preferences.start_hour ?? 5,
                end_hour: preferences.end_hour ?? 20,
                group_by: preferences.group_by || 'spot',
            });
        }
    }, [preferences]);

    // Auto-save: debounce so dragging a slider doesn't fire a request per pixel,
    // and skip the save triggered by the initial load-from-server population above.
    useEffect(() => {
        if (!preferencesLoadedRef.current || skipNextSaveRef.current) {
            skipNextSaveRef.current = false;
            return;
        }

        const timeoutId = setTimeout(() => {
            dispatch(updateAlertPreferences(formData));
        }, 700);

        return () => clearTimeout(timeoutId);
    }, [formData, dispatch]);

    const toggleSpot = (spotId) => {
        setFormData(prev => ({
            ...prev,
            spot_ids: prev.spot_ids.includes(spotId)
                ? prev.spot_ids.filter(id => id !== spotId)
                : [...prev.spot_ids, spotId]
        }));
    };

    const orderedSpots = getOrderedSpots(savedSpots, spotOrder);
    const allSpotsSelected = orderedSpots.length > 0 && orderedSpots.every(spot => formData.spot_ids.includes(spot.id));

    const toggleAllSpots = () => {
        setFormData(prev => ({
            ...prev,
            spot_ids: allSpotsSelected ? [] : orderedSpots.map(spot => spot.id)
        }));
    };

    return (
        <>
            <Helmet>
                <title>Alerts | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />
            <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
                <h1 className='text-2xl font-semibold mb-1 mt-2 flex items-center gap-2'>
                    Alerts
                </h1>
                <p className='text-custom-light-grey mt-2 max-w-[600px] text-sm'>
                    Get a single email digest whenever your watched spots have days that meet your
                    minimum rating &mdash; no need to check every spot yourself.
                </p>

                <nav className='mt-6 mb-5 max-w-[900px]'>
                    <ul className='flex gap-6 items-center text-sm border-b border-custom-charcoal'>
                        <li>
                            <button
                                type="button"
                                onClick={() => setActiveTab('settings')}
                                className={`font-semibold py-3 border-b-2 block ${activeTab === 'settings' ? 'border-custom-light-blue text-custom-light-blue' : 'border-transparent text-custom-light-grey'}`}
                            >
                                Settings
                            </button>
                        </li>
                        <li>
                            <button
                                type="button"
                                onClick={() => setActiveTab('report')}
                                className={`font-semibold py-3 border-b-2 block ${activeTab === 'report' ? 'border-custom-light-blue text-custom-light-blue' : 'border-transparent text-custom-light-grey'}`}
                            >
                                Live Report
                            </button>
                        </li>
                    </ul>
                </nav>

                {activeTab === 'settings' && (
                <form className='max-w-[900px] mt-5'>

                    <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
                            className='w-full flex items-center justify-between p-4 text-left'
                        >
                            <span>{formData.enabled ? 'Alerts On' : 'Alerts Off'}</span>
                            <span className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${formData.enabled ? 'bg-custom-light-blue' : 'bg-custom-dark-blue'}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </span>
                        </button>

                        <div className='p-4'>
                            <div className='flex items-center justify-between mb-3'>
                                <p className='text-lg'>Watch These Spots</p>
                                {orderedSpots.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={toggleAllSpots}
                                        className='text-xs font-medium text-custom-light-blue hover:text-custom-light-blue/80 transition-colors'
                                    >
                                        {allSpotsSelected ? 'Deselect All' : 'Select All'}
                                    </button>
                                )}
                            </div>
                            {orderedSpots.length === 0 ? (
                                <p className='text-sm text-custom-light-grey'>Save a spot first to add it here.</p>
                            ) : (
                                <div className='rounded-lg bg-custom-dark/30 divide-y divide-custom-charcoal overflow-hidden'>
                                    {orderedSpots.map((spot) => (
                                        <Checkbox
                                            key={spot.id}
                                            name={`spot-${spot.id}`}
                                            label={spot.name}
                                            checked={formData.spot_ids.includes(spot.id)}
                                            onChange={() => toggleSpot(spot.id)}
                                            className='px-4 py-3'
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='p-4'>
                            <DaysOfWeekPicker
                                label="Email Me On These Days"
                                value={formData.days_of_week}
                                onChange={(value) => setFormData(prev => ({ ...prev, days_of_week: value }))}
                            />
                        </div>

                        <div className='p-4'>
                            <label className='block text-sm font-medium text-custom-light-grey mb-3'>
                                Forecast Horizon ({formData.forecast_days} day{formData.forecast_days === 1 ? '' : 's'})
                            </label>
                            <input
                                type="range"
                                min={1}
                                max={MAX_FORECAST_DAYS}
                                step={1}
                                value={formData.forecast_days}
                                onChange={(e) => setFormData(prev => ({ ...prev, forecast_days: Number(e.target.value) }))}
                                className='w-full'
                            />
                        </div>

                        <div className='p-4'>
                            <DualSlider
                                label="Alert Time Range"
                                min={0}
                                max={24}
                                step={1}
                                minValue={formData.start_hour}
                                maxValue={formData.end_hour}
                                onMinChange={(value) => setFormData(prev => ({ ...prev, start_hour: value }))}
                                onMaxChange={(value) => setFormData(prev => ({ ...prev, end_hour: value }))}
                                unit=":00"
                            />
                            <p className='text-xs text-custom-light-grey'>
                                Only hours in this window count towards a qualifying day. Widen it toward 0:00–24:00 if you're happy to surf at night.
                            </p>
                        </div>

                        <div className='p-4'>
                            <GroupByPicker
                                label="Group Email By"
                                value={formData.group_by}
                                onChange={(value) => setFormData(prev => ({ ...prev, group_by: value }))}
                            />
                        </div>

                        <div className='p-4'>
                            <label className='block text-sm font-medium text-custom-light-grey mb-3'>
                                Minimum Rating ({Number(formData.min_rating).toFixed(1)}★)
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={5}
                                step={0.5}
                                value={formData.min_rating}
                                onChange={(e) => setFormData(prev => ({ ...prev, min_rating: Number(e.target.value) }))}
                                className='w-full'
                            />
                            <p className='text-xs text-custom-light-grey mt-2'>
                                Only days that reach this rating or higher will be included in your email.
                            </p>
                        </div>
                    </div>
                </form>
                )}

                {activeTab === 'report' && (
                <div className='max-w-[900px] mt-5'>
                    <div className='flex items-center justify-between mb-3'>
                        <h2 className='text-lg font-semibold'>Live Report</h2>
                        <button
                            type="button"
                            onClick={() => dispatch(fetchAlertReport({ force: true }))}
                            disabled={isLoadingReport}
                            className='text-xs font-medium text-custom-light-blue hover:text-custom-light-blue/80 transition-colors'
                        >
                            {isLoadingReport ? 'Checking...' : 'Refresh'}
                        </button>
                    </div>

                    {report?.generated_at && (
                        <p className='text-xs text-custom-light-grey mb-3'>
                            Generated {new Date(report.generated_at).toLocaleString()}
                        </p>
                    )}

                    <div className='max-w-[260px] mb-4'>
                        <GroupByPicker
                            label="Arrange By"
                            value={reportGroupBy}
                            onChange={setReportGroupBy}
                        />
                    </div>

                    {isLoadingReport && !report ? (
                        <p className='text-sm text-custom-light-grey'>Checking current conditions...</p>
                    ) : !report || report.spot_digests.length === 0 ? (
                        <p className='text-sm text-custom-light-grey'>No spots currently meet your alert conditions.</p>
                    ) : reportGroupBy === 'day' ? (
                        <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                            {Object.entries(report.day_digests || {}).map(([date, spotsOnDay]) => (
                                <div key={date} className='p-4'>
                                    <p className='font-semibold mb-3'>{formatDigestDate(date)}</p>
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                            <thead>
                                                <tr className='text-left text-xs text-custom-light-grey border-b border-custom-charcoal'>
                                                    <th className='py-2 pr-4 font-medium'>Spot</th>
                                                    <th className='py-2 pr-4 font-medium'>Best Time</th>
                                                    <th className='py-2 pr-4 font-medium'>Rating</th>
                                                    <th className='py-2 pr-4 font-medium'>Wave</th>
                                                    <th className='py-2 pr-4 font-medium'>Period</th>
                                                    <th className='py-2 font-medium'>Wind</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {spotsOnDay.map((day) => (
                                                    <tr key={`${day.spot_name}-${day.time}`} className='border-b border-custom-charcoal/50 last:border-0'>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>{day.spot_name}</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap text-custom-light-grey'>{day.time.slice(11, 16)}</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap text-custom-light-blue font-semibold'>{Number(day.score).toFixed(1)}&#9733;</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>
                                                            {day.wave_height_max}{day.wave_height_unit}
                                                            {day.wave_direction !== null && (
                                                                <>
                                                                    {' '}<span className='inline-block' style={{ transform: `rotate(${directionArrowRotation(day.wave_direction)}deg)` }}>&uarr;</span>{' '}
                                                                    {getDirectionText(day.wave_direction)}
                                                                </>
                                                            )}
                                                        </td>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>{day.wave_period_max}{day.wave_period_unit}</td>
                                                        <td className='py-2 whitespace-nowrap'>
                                                            {day.wind_speed_max}{day.wind_speed_unit}
                                                            {day.wind_direction !== null && (
                                                                <>
                                                                    {' '}<span className='inline-block' style={{ transform: `rotate(${directionArrowRotation(day.wind_direction)}deg)` }}>&uarr;</span>{' '}
                                                                    {getDirectionText(day.wind_direction)}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                            {report.spot_digests.map((spotDigest) => (
                                <div key={spotDigest.name} className='p-4'>
                                    <p className='font-semibold mb-3'>{spotDigest.name}</p>
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                            <thead>
                                                <tr className='text-left text-xs text-custom-light-grey border-b border-custom-charcoal'>
                                                    <th className='py-2 pr-4 font-medium'>Date</th>
                                                    <th className='py-2 pr-4 font-medium'>Best Time</th>
                                                    <th className='py-2 pr-4 font-medium'>Rating</th>
                                                    <th className='py-2 pr-4 font-medium'>Wave</th>
                                                    <th className='py-2 pr-4 font-medium'>Period</th>
                                                    <th className='py-2 font-medium'>Wind</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {spotDigest.days.map((day) => (
                                                    <tr key={day.date} className='border-b border-custom-charcoal/50 last:border-0'>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>{formatDigestDate(day.date)}</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap text-custom-light-grey'>{day.time.slice(11, 16)}</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap text-custom-light-blue font-semibold'>{Number(day.score).toFixed(1)}&#9733;</td>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>
                                                            {day.wave_height_max}{day.wave_height_unit}
                                                            {day.wave_direction !== null && (
                                                                <>
                                                                    {' '}<span className='inline-block' style={{ transform: `rotate(${directionArrowRotation(day.wave_direction)}deg)` }}>&uarr;</span>{' '}
                                                                    {getDirectionText(day.wave_direction)}
                                                                </>
                                                            )}
                                                        </td>
                                                        <td className='py-2 pr-4 whitespace-nowrap'>{day.wave_period_max}{day.wave_period_unit}</td>
                                                        <td className='py-2 whitespace-nowrap'>
                                                            {day.wind_speed_max}{day.wind_speed_unit}
                                                            {day.wind_direction !== null && (
                                                                <>
                                                                    {' '}<span className='inline-block' style={{ transform: `rotate(${directionArrowRotation(day.wind_direction)}deg)` }}>&uarr;</span>{' '}
                                                                    {getDirectionText(day.wind_direction)}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                )}
            </div>
        </>
    )
}

export default Alerts
